import clsx from "clsx";
import { Edit3 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Alert } from "@/components/Alert/Alert";
import { Button } from "@/components/Button/Button";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { getActiveJobRole } from "@/store/assistant/selectors";
import { openModal } from "@/store/modal/slice";
import { getUserPreferences } from "@/store/user/selectors";
import { ModalTypes } from "@/types/modal";
import { CLARITY_EVENT_NAMES } from "@/types/tracking";
import { trackClarityEvent, trackPostHogEvent } from "@/utils/tracking";

export const JobRoleSelector = ({
    noMargin = false,
}: {
    noMargin?: boolean;
}) => {
    const activeJobRole = useAppSelector(getActiveJobRole);
    const [selectedJobRole, setSelectedJobRole] = useState(activeJobRole);
    const dispatch = useAppDispatch();
    const userPreferences = useAppSelector(getUserPreferences);

    useEffect(() => {
        setSelectedJobRole(activeJobRole);
    }, [activeJobRole]);

    useEffect(() => {
        // If the active job role is not in the user preferences, set it to nothing
        if (!userPreferences?.jobRoles?.includes(activeJobRole)) {
            setSelectedJobRole("");
        }
    }, [userPreferences, activeJobRole]);

    const openJobRoleModal = useCallback(() => {
        dispatch(openModal({ modalType: ModalTypes.ASSISTANT_JOB_ROLE }));
        trackClarityEvent(CLARITY_EVENT_NAMES.ASSISTANT_JOB_ROLE_CLICKED);
        trackPostHogEvent(CLARITY_EVENT_NAMES.ASSISTANT_JOB_ROLE_CLICKED);
    }, [dispatch]);

    return (
        <div
            className={clsx(
                "rounded-lg bg-neutral-100 px-3 py-2",
                !selectedJobRole && "flex-col",
                noMargin ? "mx-0" : "mx-4",
            )}
        >
            <div className="flex items-center gap-3">
                {selectedJobRole && (
                    <AppText variant="labelsbuttons">Your Role:</AppText>
                )}
                <Button
                    className="!h-auto min-h-7 flex-1 !px-4 !py-1"
                    rightIcon={<Edit3 size={16} />}
                    onClick={openJobRoleModal}
                >
                    {selectedJobRole ? (
                        <AppText variant="footnote">{selectedJobRole}</AppText>
                    ) : (
                        <AppText variant="footnote">Choose a role</AppText>
                    )}
                </Button>
            </div>
            {!selectedJobRole && (
                <Alert
                    className="mt-4"
                    type="info"
                    message="Choosing a role helps tailor the Assistant to your needs."
                />
            )}
        </div>
    );
};
